import { memo, useState, type KeyboardEvent } from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { Variation } from '@gokrew/models/ui.types';
import type {
  NotificationAction,
  NotificationActionIntent,
  NotificationActionRequest,
  NotificationItem,
} from '@gokrew/models/notifications';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { getInitialsFromName } from '@/utility/utility';
import {
  formatNotificationTime,
  getNotificationCategoryTone,
} from '@/utility/notifications';
import styles from './Notifications.module.css';

/** Only `intent` values the shared Button has a variant for are mapped. */
const ACTION_VARIANT: Record<NotificationActionIntent, Variation> = {
  primary: 'primary',
  danger: 'dangerOutline',
  neutral: 'outline',
};

/**
 * An action carrying a `request` — the only kind rendered as an inline CTA, so
 * a handler never has to assert the field is there.
 */
export type NotificationInlineAction = NotificationAction & {
  request: NotificationActionRequest;
};

export interface NotificationRowProps {
  item: NotificationItem;
  /** `null` when this portal has no screen for the item's subject. */
  href: string | null;
  onOpen: (item: NotificationItem, href: string | null) => void;
  onDelete: (item: NotificationItem) => void;
  onAction: (
    item: NotificationItem,
    action: NotificationInlineAction,
    promptValue?: string
  ) => Promise<unknown>;
}

export const NotificationRow = memo(function NotificationRow({
  item,
  href,
  onOpen,
  onDelete,
  onAction,
}: NotificationRowProps) {
  const { i18n, t } = useTranslation(TranslationNS.Notification);
  const [prompted, setPrompted] = useState<NotificationInlineAction | null>(
    null
  );
  const [promptValue, setPromptValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolved, setIsResolved] = useState(false);

  // Actions without a `request` (VIEW, COMPLETE, …) mean "open the subject",
  // which the row click already does. Stored actions are a snapshot of the
  // resource's state when it was written, so once one succeeds this row's
  // buttons are retired locally — a refetch would hand them back.
  const inlineActions = isResolved
    ? []
    : item.actions.filter(
        (action): action is NotificationInlineAction => !!action.request
      );

  const runAction = async (
    action: NotificationInlineAction,
    value?: string
  ) => {
    setIsSubmitting(true);
    try {
      await onAction(item, action, value);
      setIsResolved(true);
      setPrompted(null);
      setPromptValue('');
    } catch {
      // The mutation already toasts; a failed action keeps its buttons so the
      // refetched copy can be acted on again.
    } finally {
      setIsSubmitting(false);
    }
  };

  // An anonymous survey response must not be attributed to anyone, so it gets
  // the same non-person treatment as system events even though a user triggered
  // it — `actorName` is generic here and `context.employeeId` is null.
  const isAnonymous = item.context?.isAnonymous === true;

  // The backend's copy already names the actor ("{actorName} posted …"), so the
  // name belongs in the avatar only — printing it again would duplicate it.
  const actorName = isAnonymous
    ? t('actor.anonymous')
    : item.context?.actorName?.trim() || t('actor.system');
  const isSystem = isAnonymous || !item.context?.actorUserId;
  const isClickable = !!href;

  // Handlers only where the role, tabIndex and cursor already advertise them:
  // a row with nowhere to go must not mark itself read on a click no keyboard
  // can reach.
  const handleClick = isClickable ? () => onOpen(item, href) : undefined;
  const handleKeyDown = isClickable
    ? (event: KeyboardEvent<HTMLLIElement>) => {
        // Only while the row itself holds focus — Enter or Space on an inline
        // action button must not open the subject as well.
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen(item, href);
      }
    : undefined;

  return (
    <li
      className={`${styles.row} ${item.isRead ? '' : styles.rowUnread} ${
        isClickable ? styles.rowClickable : ''
      }`}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span
        className={`${styles.avatar} ${isSystem ? styles.avatarSystem : ''}`}
        aria-hidden="true"
      >
        {getInitialsFromName(actorName)}
      </span>

      <div className={styles.body}>
        <p className={styles.title}>{item.title}</p>
        {item.message && <p className={styles.message}>{item.message}</p>}

        <div className={styles.meta}>
          <span className={styles.time}>
            {formatNotificationTime(item.createdAt, i18n.language)}
          </span>
          <Chip
            size="xs"
            type={getNotificationCategoryTone(item.category)}
            className={styles.categoryChip}
          >
            {t(`category.${item.category}`, { defaultValue: item.category })}
          </Chip>
        </div>

        {!!inlineActions.length && (
          <div
            className={styles.actions}
            onClick={(event) => event.stopPropagation()}
          >
            {prompted ? (
              <>
                <textarea
                  className={styles.promptInput}
                  rows={2}
                  autoFocus
                  value={promptValue}
                  disabled={isSubmitting}
                  placeholder={t(`prompt.${prompted.request.prompt?.kind}`, {
                    defaultValue: t('prompt.reason'),
                  })}
                  onChange={(event) => setPromptValue(event.target.value)}
                />
                <div className={styles.promptButtons}>
                  <Button
                    size="xs"
                    variant={ACTION_VARIANT[prompted.intent]}
                    disabled={isSubmitting || !promptValue.trim()}
                    onClick={() => runAction(prompted, promptValue.trim())}
                  >
                    {prompted.label}
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={isSubmitting}
                    onClick={() => {
                      setPrompted(null);
                      setPromptValue('');
                    }}
                  >
                    {t('panel.action_cancel')}
                  </Button>
                </div>
              </>
            ) : (
              inlineActions.map((action) => (
                <Button
                  key={action.id}
                  size="xs"
                  variant={ACTION_VARIANT[action.intent]}
                  disabled={isSubmitting}
                  onClick={() =>
                    action.request.prompt
                      ? setPrompted(action)
                      : runAction(action)
                  }
                >
                  {action.label}
                </Button>
              ))
            )}
          </div>
        )}
      </div>

      <div className={styles.rowEnd}>
        {!item.isRead && (
          <span className={styles.unreadDot} aria-hidden="true" />
        )}
        <button
          type="button"
          className={styles.deleteBtn}
          aria-label={t('panel.delete')}
          title={t('panel.delete')}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(item);
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
});
